/**
 * stale-while-revalidate な in-memory fetch cache (steering 551 / T6)。
 *
 * **依存は追加しない** (SWR / TanStack Query を入れると package.json / lockfile が動く)。
 * `useFetch` の返値 shape を変えずに、再訪時の即描画と同一 URL の request dedup だけを足す。
 *
 * 設計上の要点:
 *
 * 1. **identity を必ず key に含める。** `userId | orgId | role | X-Client-Id` を namespace にし、
 *    identity が変わったら全 entry を捨てる。cache は BE の認可を代替しない — fresh hit では
 *    request 自体を出さないので **その時点で認可は再評価されない**。安全性は
 *    (a) namespace 分離 (b) identity 変更時の全 clear (c) staleness 境界 の 3 点で担保する。
 *
 * 2. **request の所有者は entry であって component ではない。** component の unmount では
 *    abort せず subscriber を外すだけ。subscriber が 0 になっても即 abort せず猶予を置く
 *    (route 遷移中の unmount → 即 remount で request を無駄に捨てないため)。
 *
 * 3. **fetch が失敗したら entry の data を破棄する。** stale を残すと次の mount で
 *    cache hit として **古い数字が再表示される**。ダッシュボードは数字を出す画面なので、
 *    可用性より「古い数字を見せない」を優先する (現行 `useApi` が失敗時に data を null に
 *    する挙動とも一致する)。
 *
 * 4. **永続化しない。** `api/client.ts` の 401 経路は `logout()` を通らずに
 *    `window.location.href = '/login'` するため、in-memory でなければ次のユーザーへ残る。
 */

/** fresh とみなす期間 (ms)。これを過ぎると stale = 即返し + background revalidate。 */
export const CACHE_TTL_MS = 30_000;

/** subscriber が 0 になってから実際に abort するまでの猶予 (ms)。 */
export const ABORT_GRACE_MS = 250;

/** 保持する entry の上限 (LRU)。`/clients` 級の response を無制限に抱えないため。 */
export const CACHE_MAX_ENTRIES = 100;

interface CacheEntry {
  /** 直近の成功データ。失敗時は **破棄** して null にする。 */
  data: unknown;
  /** 直近の失敗メッセージ。次の成功でのみ clear する。 */
  error: string | null;
  /** 成功時刻 (ms)。0 は「データ無し」。 */
  updatedAt: number;
  /** 進行中の request (dedup 用)。 */
  inflight: Promise<unknown> | null;
  /** entry が所有する AbortController (component は持たない)。 */
  controller: AbortController | null;
  /** 変更通知先。 */
  subscribers: Set<() => void>;
  /** LRU 用の最終参照時刻。 */
  lastAccess: number;
}

const store = new Map<string, CacheEntry>();

/** 現在の identity namespace。null の間 (認証 hydration 中) は cache を使わない。 */
let currentScope: string | null = null;

/**
 * cache key。区切り文字の衝突を避けるため **JSON 配列**で合成する。
 * (制御文字を区切りに使うと、本文へ混入したときに気づきにくい。)
 */
function makeKey(scope: string, url: string): string {
  return JSON.stringify([scope, url]);
}

function getOrCreate(key: string): CacheEntry {
  let entry = store.get(key);
  if (!entry) {
    entry = {
      data: null,
      error: null,
      updatedAt: 0,
      inflight: null,
      controller: null,
      subscribers: new Set(),
      lastAccess: Date.now(),
    };
    store.set(key, entry);
    evictIfNeeded();
  }
  return entry;
}

/** LRU: 上限を超えたら最終参照が最も古い entry から捨てる (購読中の entry は残す)。 */
function evictIfNeeded(): void {
  if (store.size <= CACHE_MAX_ENTRIES) return;
  const candidates = [...store.entries()]
    .filter(([, e]) => e.subscribers.size === 0 && e.inflight === null)
    .sort((a, b) => a[1].lastAccess - b[1].lastAccess);
  let over = store.size - CACHE_MAX_ENTRIES;
  for (const [key] of candidates) {
    if (over <= 0) break;
    store.delete(key);
    over -= 1;
  }
}

function notify(entry: CacheEntry): void {
  for (const listener of [...entry.subscribers]) listener();
}

/**
 * identity namespace を設定する。**変化したら全 entry を破棄**する。
 *
 * org 切替 / role 変更 / 別ユーザーの再ログインを 1 本の規律で覆う。
 * `null` を渡すと「identity 未確定」となり、以降 cache は読まれも書かれもしない。
 */
export function setIdentityScope(scope: string | null): void {
  if (scope === currentScope) return;
  currentScope = scope;
  clearAll();
}

/**
 * 現在の identity namespace (テスト・診断用。加えて、個人設定の書き込みの列 (`hooks/useLocalePreference`)
 * が、待っている間に利用者が替わったかの判定に使う)。
 */
export function getIdentityScope(): string | null {
  return currentScope;
}

/** 全 entry を破棄する (logout / identity 変更)。進行中の request も abort する。 */
export function clearAll(): void {
  for (const entry of store.values()) {
    entry.controller?.abort();
    entry.controller = null;
    entry.inflight = null;
    // 破棄前に購読者へ通知して、残った component が古い値を見続けないようにする。
    entry.data = null;
    entry.updatedAt = 0;
    notify(entry);
  }
  store.clear();
}

export interface CacheSnapshot<T> {
  data: T | null;
  error: string | null;
  /** entry が存在し、かつ TTL 内か。 */
  isFresh: boolean;
  /** データを持っているか (fresh / stale を問わない)。 */
  hasData: boolean;
}

/** 現在の状態を読む (fetch は起こさない)。scope 未確定なら常に空。 */
export function read<T>(url: string): CacheSnapshot<T> {
  if (currentScope === null) return { data: null, error: null, isFresh: false, hasData: false };
  const entry = store.get(makeKey(currentScope, url));
  if (!entry) return { data: null, error: null, isFresh: false, hasData: false };
  entry.lastAccess = Date.now();
  const hasData = entry.updatedAt > 0;
  return {
    data: entry.data as T | null,
    error: entry.error,
    isFresh: hasData && Date.now() - entry.updatedAt < CACHE_TTL_MS,
    hasData,
  };
}

/** 変更購読。戻り値を呼ぶと解除し、subscriber が 0 になれば猶予後に abort する。 */
export function subscribe(url: string, listener: () => void): () => void {
  if (currentScope === null) return () => {};
  const key = makeKey(currentScope, url);
  const entry = getOrCreate(key);
  entry.subscribers.add(listener);
  entry.lastAccess = Date.now();

  return () => {
    entry.subscribers.delete(listener);
    // **unmount では abort しない。** 他の consumer が同じ request を待っている可能性があり、
    // ここで abort すると他人の request を殺す。0 になったときだけ、猶予を置いて再確認する。
    if (entry.subscribers.size === 0) {
      setTimeout(() => {
        if (entry.subscribers.size === 0 && entry.inflight) {
          entry.controller?.abort();
          entry.controller = null;
          entry.inflight = null;
        }
      }, ABORT_GRACE_MS);
    }
  };
}

/** abort 由来のエラーか (現行 useApi と同じく error に昇格させない)。 */
function isAbort(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  // `api/client.ts` は **事前 abort 済み** signal に対して plain Error を投げる。
  return err instanceof Error && err.message === 'リクエストが中止されました';
}

export interface FetchOptions {
  /** true なら TTL を無視して必ず fetch する (`refetch()` の意味論)。 */
  force?: boolean;
}

/**
 * cache 経由で fetch する。
 *
 * - inflight があれば **それに相乗り**する (同一 key の dedup)
 * - fresh かつ force でなければ fetch しない
 * - 失敗したら **entry の data を破棄**する (stale を残さない)
 */
export function fetchThrough<T>(
  url: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options: FetchOptions = {},
): Promise<T | null> {
  if (currentScope === null) {
    // identity 未確定: cache を読まず書かず、素の fetch に倒す (fail-safe)。
    // `Promise.resolve` で包むのは、fetcher が promise を返さない実装 (test の mock 等) でも
    // 従来の `await fetcher()` と同じ寛容さを保つため。
    return Promise.resolve(fetcher(new AbortController().signal)).catch((err: unknown) => {
      if (isAbort(err)) return null;
      throw err;
    });
  }

  const key = makeKey(currentScope, url);
  const entry = getOrCreate(key);
  entry.lastAccess = Date.now();

  if (entry.inflight) return entry.inflight as Promise<T | null>;

  const hasData = entry.updatedAt > 0;
  const isFresh = hasData && Date.now() - entry.updatedAt < CACHE_TTL_MS;
  if (isFresh && !options.force) {
    return Promise.resolve(entry.data as T);
  }

  const controller = new AbortController();
  entry.controller = controller;

  const promise = Promise.resolve(fetcher(controller.signal))
    .then((result) => {
      entry.data = result;
      entry.updatedAt = Date.now();
      // error は **成功したときにだけ** clear する (開始時に消すと一瞬消えてまた出る)。
      entry.error = null;
      entry.inflight = null;
      entry.controller = null;
      notify(entry);
      return result as T | null;
    })
    .catch((err: unknown) => {
      entry.inflight = null;
      entry.controller = null;
      if (isAbort(err)) {
        // abort は state を変えない (現行 useApi と同じ)。
        return null;
      }
      // **失敗したら data を破棄する。** stale を残すと次 mount で古い数字が再表示される。
      entry.data = null;
      entry.updatedAt = 0;
      entry.error = err instanceof Error ? err.message : 'データの取得に失敗しました';
      notify(entry);
      throw err;
    });

  entry.inflight = promise as Promise<unknown>;
  return promise;
}

/**
 * データを持つ entry のうち、URL が条件に合うものを「古い」(TTL 切れ) にする。データは残すので
 * spinner は出さず、次の read / mount / refetch で取り直す。
 *
 * 書き込みの後、いま画面に出していない別ページ (ページ送りの先頭など) を TTL まで古いまま
 * 見せないために使う (受任通知 FAX 送信履歴、2026-10-08)。
 * `updatedAt` を「データあり (> 0) で必ず TTL 切れ」の値 (= 1) にする。進行中の request には触れない。
 */
export function markStale(matches: (url: string) => boolean): void {
  if (currentScope === null) return;
  for (const [key, entry] of store) {
    if (entry.updatedAt <= 0) continue;
    const parsed: unknown = JSON.parse(key);
    if (
      Array.isArray(parsed) &&
      parsed[0] === currentScope &&
      typeof parsed[1] === 'string' &&
      matches(parsed[1])
    ) {
      entry.updatedAt = STALE_UPDATED_AT;
    }
  }
}

/** `markStale` が付ける「データあり・必ず TTL 切れ」の成功時刻。 */
const STALE_UPDATED_AT = 1;

/** テスト用: 全状態を初期化する (identity scope も含む)。 */
/** 書込み前からの読込が終わった後に取り直し、古い応答との競合を避ける。 */
export async function refreshAfterWrite<T>(
  url: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
): Promise<T | null> {
  const scope = currentScope;
  const pending = scope === null ? null : store.get(makeKey(scope, url))?.inflight;
  if (pending) await pending.catch(() => null);
  // 待機中に利用者・組織が変わったら、新しいidentityで旧案件を読まない。
  if (currentScope !== scope) return null;
  return fetchThrough(url, fetcher, { force: true });
}

export function __resetForTest(): void {
  clearAll();
  currentScope = null;
}
